import { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { getHoursAndMinutes } from './dateTimeConverters';
import { wrapFormattedPartsUnitInSpan } from './utils';

/**
 * Formats time as HH:mm:ss
 * @param totalSeconds Time, in seconds
 * @returns Time formatted as HH:mm:ss
 */
export function formatTimeHhMmSs(totalSeconds: number): string {
  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / (60 * 60));

  const secondsString = String(seconds).padStart(2, '0');
  const minutesString = String(minutes).padStart(2, '0');

  if (hours) {
    return `${hours}:${minutesString}:${secondsString}`;
  }
  return `${minutesString}:${secondsString}`;
}
/**
 * Converts seconds to hours and minutes and wraps the units in spans so they can be styled differently than the numeric values
 * @param intl Intl object
 * @param seconds Seconds value
 * @param abbreviateMultiDay If true, will return "Multi-day" if the hours are greater than or equal to 12
 * @param unitSpace Include a space before the unit
 * @returns A fragment with the converted time values and the units, wrapped in spans
 */
export function getFormattedHoursMinutesWithUnitsInSpans(
  intl: IntlShape,
  seconds: number,
  unitSpace?: boolean,
  abbreviateMultiDay?: boolean
): ReactNode {
  const { hours, minutes } = getHoursAndMinutes(seconds);

  if (hours > 999) {
    return wrapFormattedPartsUnitInSpan(intl.formatNumberToParts(hours, { style: 'unit', unit: 'hour', unitDisplay: 'narrow' }), unitSpace);
  }

  if (hours >= 12 && abbreviateMultiDay) {
    return <span>{intl.formatMessage({ defaultMessage: 'Multi-day' })}</span>;
  }

  if (hours === 0) {
    return intl.formatMessage(
      { defaultMessage: '{minutes}<span>m</span>' },
      { span: (text: ReactNode) => <span key={text?.toString()}>{text}</span>, hours, minutes }
    );
  }

  return intl.formatMessage(
    { defaultMessage: '{hours}<span>h</span> {minutes}<span>m</span>' },
    { span: (text: ReactNode) => <span key={text?.toString()}>{text}</span>, hours, minutes }
  );
}

export type MinutesToFormattedTimeParams = {
  intl: IntlShape;
  totalMinutes: number;
  isEstimate?: boolean;
};

/**
 * Formats total minutes as a duration string (e.g. "2h 30m", "Multi-day", or "Est. 2h 30m").
 * @param params.intl Intl object
 * @param params.totalMinutes Duration in minutes
 * @param params.isEstimate If true, prefixes with "Est. "
 * @returns Formatted duration string
 */
export function minutesToFormattedTime({ intl, totalMinutes, isEstimate = false }: MinutesToFormattedTimeParams): string {
  const format = (timeString: string) => (isEstimate ? `${intl.formatMessage({ defaultMessage: 'Est.' })} ${timeString}` : timeString);
  const { hours, minutes } = getHoursAndMinutes(totalMinutes * 60);
  const minutesTranslated = intl.formatMessage({
    id: 'minute.abbreviated',
    defaultMessage: 'm'
  });
  if (hours >= 12) {
    return format(intl.formatMessage({ defaultMessage: 'Multi-day' }));
  }
  if (hours === 0 && minutes === 0) {
    return format(`0h 0${minutesTranslated}`);
  }
  return format(hours > 0 ? `${hours}h ${minutes}${minutesTranslated}` : `${minutes}${minutesTranslated}`);
}

/**
 * @param date Date or string to format
 * @param languageRegionCode Language region code
 * @param longMonth Whether to use long or shortmonth names (ex: January or Jan)
 * @returns A date string in the format of "January 1, 2021" or "Jan 1, 2021".
 * For non-en-US locales, formatting adapts to the locale. Examples:
 *   - "fr-FR": "9 décembre 2025" or "9 déc. 2025"
 *   - "de-DE": "9. Dezember 2025" or "9. Dez. 2025"
 */
export function monthDayYear(date: Date | string, languageRegionCode: LanguageRegionCode = 'en-US', longMonth = true): string {
  return new Date(date).toLocaleDateString(languageRegionCode, {
    month: longMonth ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
